import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { ContentCopyOutlined, EditOutlined, ArchiveOutlined, UnarchiveOutlined } from '@mui/icons-material'
import {
    Typography,
    Select,
    CircularProgress,
    InputLabel,
    MenuItem,
    TextField,
    Button,
    Alert,
    IconButton,
    Stack,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogActions,
    DialogTitle,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Input,
    Card,
    Box,
    Divider
} from '@mui/material'
import { AxiosWithAuth, authenticationService } from '../../Utilities/authenticationService'
import { WEEKDAY_MAPPINGS, PATHS } from '../../constants'
import { AddOutlined, Delete, RestoreFromTrash } from '@mui/icons-material'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import { isMobile } from '../../Utilities/isMobile'
import EditStoreName from './EditFlows/EditStoreName'
import EditSegCode from './EditFlows/EditSegCode'

const initialStoreHours = {
    monday: { open_time: '', close_time: '' },
    tuesday: { open_time: '', close_time: '' },
    wednesday: { open_time: '', close_time: '' },
    thursday: { open_time: '', close_time: '' },
    friday: { open_time: '', close_time: '' },
    saturday: { open_time: '', close_time: '' },
    sunday: { open_time: '', close_time: '' }
}

const EditStore = ({ history, setSegCode }) => {
    const { fetchAndSetEmployments, isAdmin } = useContext(EmploymentsContext)
    const currentUser = authenticationService.currentUserValue
    const isCompanyAdmin = currentUser?.company_admin || false
    const { segCode } = useParams()
    segCode == ":segCode" && setSegCode(null)
    const [store, setStore] = useState()
    const [storeTypes, setStoreTypes] = useState([])
    const [selectedStoreTypeId, setSelectedStoreTypeId] = useState(null)
    const [storeHours, setStoreHours] = useState(initialStoreHours)
    const [irregularStoreHours, setIrregularStoreHours] = useState([])
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)
    const [irregularStoreHourError, setIrregularStoreHourError] = useState(false)
    const [confirmationOpen, setConfirmationOpen] = useState(false)
    const [revenueStaffingRows, setRevenueStaffingRows] = useState([])
    const [errorRev, setErrorRev] = useState({})
    const [newStaffinReqs, setNewStaffinReqs] = useState({})
    const [desktop, setDesktop] = useState(false)
    const [hlgError, setHlgError] = useState(false)
    const [managerPresence, setManagerPresence] = useState(true)
    const [storeNameEdit, setStoreNameEdit] = useState(false)
    const [segCodeEdit, setSegCodeEdit] = useState(false)
    const [newMaxRev, setNewMaxRev] = useState(null)
    const [updateSuccess, setUpdateSuccess] = useState(false)
    const [copyingStaffings, setCopyingStaffings] = useState(false)
    const [copySuccess, setCopySuccess] = useState(false)
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
    const [archiving, setArchiving] = useState(false)
    const [employeeCount, setEmployeeCount] = useState(0)

    const handleReturn = () => {
        // Check if we're in admin context by looking at the current path
        if (history.location.pathname.includes('/adminSettings')) {
            history.push(PATHS.adminStoreManagements)
        } else {
            history.push(PATHS.managerSettingsStores)
        }
    }
    const saveStore = async () => {
        setSaveButtonDisabled(true)

        const rev_responses = []
        const errors = {}
        for (const key in newStaffinReqs) {
            const rev = newStaffinReqs[key]
            const res = await AxiosWithAuth.post('/revenue_staffings', { revenue_staffing: rev }).catch(() => {
                errors[rev.hourly_rev_high] = true
            })
            rev_responses.push(res)
        }
        if (Object.keys(errors).length > 0) {
            setSaveButtonDisabled(false)
            setConfirmationOpen(false)
            setErrorRev(errors)
            return
        }

        let filteredStoreHours = Object.entries(storeHours)
        filteredStoreHours = filteredStoreHours.filter(([, value]) => {
            return value.open_time !== '' && value.close_time !== ''
        })
        filteredStoreHours = Object.fromEntries(filteredStoreHours)
        const filteredStoreHoursArray = Object.entries(filteredStoreHours).map(([key, value]) => {
            return {
                id: value.id,
                day: key,
                open_time: value.open_time,
                close_time: value.close_time
            }
        })
        const irregularStoreHoursArray = irregularStoreHours
            .filter(
                sh => sh.open_time !== '' && sh.close_time !== '' && sh.valid_from !== '' && sh.valid_through !== ''
            )
            .map(
                ({
                    open_time,
                    close_time,
                    day,
                    valid_from,
                    valid_through,
                    id,
                    _destroy
                }) => {
                    return {
                        open_time,
                        close_time,
                        day,
                        valid_from,
                        valid_through,
                        id,
                        _destroy
                    }
                }
            )
        const storeHoursParams = [...filteredStoreHoursArray, ...irregularStoreHoursArray]
        const storeParams = {
            store: { store_type_id: selectedStoreTypeId, store_hours_attributes: storeHoursParams }
        }

        AxiosWithAuth.put(`/stores/${segCode}`, storeParams)
            .then(() => {
                setSaveButtonDisabled(false)
                fetchAndSetEmployments()
                setConfirmationOpen(false)
                setUpdateSuccess(true)
            })
            .catch(() => {
                setSaveButtonDisabled(false)
                setConfirmationOpen(false)
                setIrregularStoreHourError(true)
            })
    }

    const createUpdateRevenueStaffing = (value, staffing) => {
        if (isNaN(value) || parseInt(value) < 0) {
            setErrorRev({ ...errorRev, [staffing.hourly_rev_high]: true })
            setHlgError(true)
            return
        } else {
            const nan = isNaN(value)
            const newReqs = newStaffinReqs
            newReqs[staffing.hourly_rev_high] = {
                ...staffing,
                store_id: store.id,
                workers_required: nan ? value : parseFloat(value)
            }
            setNewStaffinReqs(newReqs)
            setHlgError(false)
        }
        const oldErrors = { ...errorRev }
        delete oldErrors[staffing.hourly_rev_high]
        setErrorRev(oldErrors)
    }

    const copyHour = () => {
        const mondayValues = {
            open_time: storeHours['monday']['open_time'],
            close_time: storeHours['monday']['close_time']
        }
        setStoreHours({
            monday: {
                ...storeHours.monday,
                ...mondayValues
            },
            tuesday: {
                ...storeHours.tuesday,
                ...mondayValues
            },
            wednesday: {
                ...storeHours.wednesday,
                ...mondayValues
            },
            thursday: {
                ...storeHours.thursday,
                ...mondayValues
            },
            friday: {
                ...storeHours.friday,
                ...mondayValues
            },
            saturday: {
                ...storeHours.saturday,
                ...mondayValues
            },
            sunday: {
                ...storeHours.sunday,
                ...mondayValues
            }
        })
    }

    useEffect(() => {
        if (isMobile()) {
            setDesktop(false)
        } else {
            setDesktop(true)
        }
    }, [])
    useEffect(() => {
        if (isAdmin) {
            AxiosWithAuth.get(`/stores/managerPresence/${segCode}`)
                .then(res => {
                    if (res.data.message === 'No manager') setManagerPresence(false)
                })
                .catch(err => {
                    console.log(err)
                })
        }
    }, [isAdmin, segCode])

    useEffect(() => {
        AxiosWithAuth.get(`/stores/${segCode}`)
            .then(res => setStore(res.data))
            .catch(err => console.log(err))
        AxiosWithAuth.get('/store_types')
            .then(res => setStoreTypes(res.data))
            .catch(err => console.log(err))
        fetchRevenueStaffings()
    }, [segCode])
    
    const fetchRevenueStaffings = () => {
        AxiosWithAuth.get(`/revenueStaffings/store/${segCode}`).then(res => {
            const max_hourly_rev = res.data.override_staffings.sort((a, b) => b.hourly_rev_high - a.hourly_rev_high)[0]
            const overrides = res.data.override_staffings.filter(e => e.store_type_id)
            const storeTypeId = res.data.staffings[0]?.store_type_id
            const rows = max_hourly_rev ? Array.from({ length: max_hourly_rev.hourly_rev_high / 2500 }, (_, i) => (i + 1) * 2500) : []
            const staffings = []
            rows.forEach(hourly_rev_high => {
                const overrideStaffing = overrides.find(e => e.hourly_rev_high === hourly_rev_high)
                const workersRequired = overrideStaffing?.workers_required
                staffings.push({
                    hourly_rev_high,
                    hourly_rev_low: hourly_rev_high - 2499, // only used for display in this page
                    store_type_id: storeTypeId,
                    workers_required: workersRequired || '-'
                })
            })
            setRevenueStaffingRows(staffings)
        })
    }

    useEffect(() => {
        if (store) {
            setSelectedStoreTypeId(store.store_type_id)
            store.store_hours?.forEach(storeHour => {
                if (storeHour.day === 'irregular') {
                    const hasRow = irregularStoreHours.filter(sh => sh.id === storeHour.id).length > 0
                    if (!hasRow) {
                        setIrregularStoreHours(prev => [...prev, storeHour])
                    }
                } else {
                    setStoreHours(prev => {
                        return {
                            ...prev,
                            [storeHour.day]: {
                                id: storeHour.id,
                                open_time: storeHour.open_time,
                                close_time: storeHour.close_time
                            }
                        }
                    })
                }
            })
        }
    }, [store])

    const handleUpdateMaxRev = () => {
        // create revenue staffings between current max and new max if higher, delete if lower
        AxiosWithAuth.post(`/stores/${segCode}/update_max_rev`, {
            new_max_rev: newMaxRev
        }).then(() => {
            fetchRevenueStaffings()
        })
    }

    const handleCopyRevenueStaffings = () => {
        setCopyingStaffings(true)
        setCopySuccess(false)
        AxiosWithAuth.post(`/stores/${segCode}/copy_revenue_staffings`)
            .then(() => {
                setCopyingStaffings(false)
                setCopySuccess(true)
                fetchRevenueStaffings()
                setTimeout(() => setCopySuccess(false), 3000)
            })
            .catch((err) => {
                setCopyingStaffings(false)
                console.error('Error copying revenue staffings:', err)
            })
    }

    const handleOpenArchiveDialog = () => {
        // Fetch employee count before opening dialog
        AxiosWithAuth.get(`/employments/store/${segCode}`)
            .then(res => {
                setEmployeeCount(res.data.length)
                setArchiveDialogOpen(true)
            })
            .catch(err => {
                console.error('Error fetching employments:', err)
                setEmployeeCount(0)
                setArchiveDialogOpen(true)
            })
    }

    const handleArchiveStore = () => {
        setArchiving(true)
        AxiosWithAuth.post(`/stores/${segCode}/archive`)
            .then(() => {
                setArchiving(false)
                setArchiveDialogOpen(false)
                fetchAndSetEmployments()
                handleReturn()
            })
            .catch(err => {
                setArchiving(false)
                console.error('Error archiving store:', err)
            })
    }

    const handleUnarchiveStore = () => {
        setArchiving(true)
        AxiosWithAuth.post(`/stores/${segCode}/unarchive`)
            .then(() => {
                setArchiving(false)
                // Refresh store data
                AxiosWithAuth.get(`/stores/${segCode}`)
                    .then(res => setStore(res.data))
                    .catch(err => console.log(err))
                setUpdateSuccess(true)
            })
            .catch(err => {
                setArchiving(false)
                console.error('Error unarchiving store:', err)
            })
    }
    
    if (!store || !storeTypes) {
        return <CircularProgress sx={{ mx: '50%', mt: '10%' }} />
    }

    if (storeNameEdit) {
        return <EditStoreName current={store.name} />
    }

    if (segCodeEdit) {
        return <EditSegCode current={store.seg_code} />
    }

    return (
        <>
            {store?.archived && (
                <Alert severity="warning" sx={{ width: '40rem', margin: '1rem auto' }}>
                    この店舗はアーカイブされています。
                </Alert>
            )}
            {updateSuccess && (
                <Alert severity="success" sx={{ width: '40rem', margin: '1rem auto' }}>
                    店舗情報を更新しました。
                </Alert>
            )}
            {copySuccess && (
                <Alert severity="success" sx={{ width: '40rem', margin: '1rem auto' }}>
                    店舗タイプのHLG表を店舗固有のHLG表にコピーしました。
                </Alert>
            )}
            <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                <Card sx={{ p: isMobile() ? '0 1rem' : 2, width: isMobile() ? '90vw' : '50%', boxShadow: isMobile() && 'none'}} >
                    <Typography variant="h6" sx={{ mb: 2 }}>店舗タイプ</Typography>
                    <div style={{ margin: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {irregularStoreHourError && (
                            <Alert severity="error" onClose={() => setIrregularStoreHourError(false)}>
                                {store?.name} の情報を更新出来ませんでした。特別営業時間が重複している可能性があります。
                            </Alert>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', width: desktop ? '40rem' : '99%' }}>
                            {!isMobile() && <Typography
                                sx={{
                                    background: '#f5f5f5',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    height: '45px',
                                    width: '45em'
                                }}
                            >
                                店舗コード：{store.seg_code}
                                {isAdmin ? (
                                    <IconButton
                                        variant="text"
                                        color="paloBlue"
                                        onClick={() => {
                                            setSegCodeEdit(true)
                                        }}
                                    >
                                        <EditOutlined />
                                    </IconButton>
                                ) : null}
                            </Typography>}
                            <Typography
                                sx={{
                                    background: '#f5f5f5',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    height: '45px',
                                    width: '45em'
                                }}
                            >
                                店舗名：{store.name}
                                {isAdmin ? (
                                    <IconButton
                                        variant="text"
                                        color="paloBlue"
                                        onClick={() => {
                                            setStoreNameEdit(true)
                                        }}
                                    >
                                        <EditOutlined />
                                    </IconButton>
                                ) : null}
                            </Typography>
                        </div>
                        {store.store_seg_code_versions?.length > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', width: desktop ? '40rem' : '95%' }}>
                                <Typography
                                    sx={{
                                        background: '#f5f5f5',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        height: '45px',
                                        width: '45em'
                                    }}
                                >
                                    （過去の店舗コード：
                                    {[...new Set(store.store_seg_code_versions
                                        .map(version => version.seg_code)
                                        .filter(code => code !== store.seg_code))]
                                        .join(', ')}
                                    ）
                                </Typography>
                            </div>
                        )}
                        {!managerPresence && (
                            <Alert severity="warning" sx={{ width: desktop ? '45rem' : '95%', marginTop: '1rem' }}>
                                この店舗にマネージャーがいません。マネージャーを登録してください。
                            </Alert>
                        )}

                        {(isAdmin || (isCompanyAdmin && store?.company_id === currentUser?.company_id)) && (
                            <div style={{ width: desktop ? '40rem' : '95%', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                {isAdmin && (
                                    <Button
                                        sx={{ mt: '1rem' }}
                                        variant="contained"
                                        color="paloBlue"
                                        onClick={() => {
                                            history.push(PATHS.adminStoreUserList.replace(':segCode', store.seg_code))
                                        }}
                                    >
                                        この店舗の従業員リスト
                                    </Button>
                                )}
                                {store.archived ? (
                                    <Button
                                        sx={{ mt: '1rem' }}
                                        variant="contained"
                                        color="success"
                                        startIcon={archiving ? <CircularProgress size={20} /> : <UnarchiveOutlined />}
                                        onClick={handleUnarchiveStore}
                                        disabled={archiving}
                                    >
                                        {archiving ? 'アーカイブ解除中...' : 'アーカイブを解除'}
                                    </Button>
                                ) : (
                                    <Button
                                        sx={{ mt: '1rem' }}
                                        variant="outlined"
                                        color="error"
                                        startIcon={<ArchiveOutlined />}
                                        onClick={handleOpenArchiveDialog}
                                    >
                                        店舗をアーカイブ
                                    </Button>
                                )}
                            </div>
                        )}
                        {store && storeTypes.length > 0 && selectedStoreTypeId && (
                            <div style={{ display: 'flex', justifyContent: 'center', width: desktop ? '40rem' : '95%' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        width: '45em'
                                    }}
                                >
                                    <InputLabel sx={{ my: '0.5rem' }} id="storeType">
                                        店舗タイプ
                                    </InputLabel>
                                    <Select
                                        data-testid="storeTypeSelect"
                                        fullWidth
                                        labelId="storeType"
                                        placeholder="選択してください"
                                        value={selectedStoreTypeId}
                                        onChange={e => setSelectedStoreTypeId(e.target.value)}
                                    >
                                        {storeTypes.map(storeType => (
                                            <MenuItem key={storeType.id} value={storeType.id}>
                                                {storeType.type_name} - {storeType.brand_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <InputLabel sx={{ mt: '1rem', mb: '0.5rem' }} id="storeHours">
                                        通常営業時間
                                    </InputLabel>
                                </div>
                            </div>
                        )}
                        {storeHours && (
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'baseline',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <div style={{ width: desktop ? '40rem' : '95%' }}>
                                        {Object.keys(WEEKDAY_MAPPINGS).map(day => (
                                            <div
                                                key={day}
                                                style={{
                                                    margin: '1rem auto',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                data-testid="opening-hours"
                                            >
                                                <Typography sx={{ m: '0.5rem' }}>{WEEKDAY_MAPPINGS[day]}</Typography>
                                                <TextField
                                                    fullWidth
                                                    type="time"
                                                    label="開店時間"
                                                    sx={{ mx: '0.5rem' }}
                                                    style={{ width: desktop ? '' : '8rem' }}
                                                    value={storeHours[day].open_time}
                                                    onChange={e => {
                                                        setStoreHours(prev => ({
                                                            ...prev,
                                                            [day]: {
                                                                ...prev[day],
                                                                open_time: e.target.value
                                                            }
                                                        }))
                                                    }}
                                                    inputProps={{ step: 900 }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    type="time"
                                                    label="閉店時間"
                                                    sx={{ mx: '0.5rem' }}
                                                    style={{ width: desktop ? '' : '8rem' }}
                                                    value={storeHours[day].close_time}
                                                    onChange={e => {
                                                        setStoreHours(prev => ({
                                                            ...prev,
                                                            [day]: {
                                                                ...prev[day],
                                                                close_time: e.target.value
                                                            }
                                                        }))
                                                    }}
                                                    inputProps={{ step: 900 }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <IconButton onClick={copyHour}>
                                        <ContentCopyOutlined />
                                    </IconButton>
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{ width: desktop ? '40rem' : '95%' }}>
                                <Typography variant="body1" sx={{ mt: '1rem', mb: '0.5rem' }} id="irregularStoreHours">
                                    特別営業時間
                                </Typography>
                                <div style={{ maxWidth: '90vw' }}>
                                    <Typography sx={{ color: 'gray', mb: '1.5rem' }} variant="body2">
                                        新たに特別営業時間を設定する場合は、下の「＋特別営業時間を追加」ボタンを押し、開始日・終了日・開店時間・閉店時間をセットして、「保存する」を押してください。削除する場合は、ゴミ箱アイコンを押してから、下の「保存する」ボタンを押してください。再度ゴミ箱をクリックすると、削除候補から外れます。
                                    </Typography>
                                </div>
                                <div style={{ maxWidth: '95vw' }}>
                                    <Button
                                        variant="outlined"
                                        color="paloBlue"
                                        sx={{
                                            width: desktop ? '45em' : '100%',
                                            marginBottom: '2rem'
                                        }}
                                        startIcon={<AddOutlined />}
                                        onClick={() => {
                                            setIrregularStoreHours(prev => [
                                                ...prev,
                                                {
                                                    valid_from: '',
                                                    valid_through: '',
                                                    open_time: storeHours['monday']['open_time'],
                                                    close_time: storeHours['monday']['close_time'],
                                                    day: 'irregular'
                                                }
                                            ])
                                        }}
                                    >
                                        特別営業時間を追加
                                    </Button>
                                    {irregularStoreHours.map((sh, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Typography sx={{ my: '0.3em', width: '0.5em' }} id="irregularStoreHours">
                                                {idx + 1}
                                            </Typography>

                                            <Stack key={idx} spacing={2} sx={{ mb: '1rem', justifyContent: 'center' }}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                        justifyContent: 'space-evenly'
                                                    }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label="開始日"
                                                        disabled={irregularStoreHours[idx]._destroy}
                                                        helperText={irregularStoreHours[idx]._destroy ? '削除候補' : ''}
                                                        sx={{ width: desktop ? '46%' : '8rem', marginBottom: '1em' }}
                                                        value={sh.valid_from}
                                                        onChange={e => {
                                                            setIrregularStoreHours(prev => {
                                                                const newIrregularStoreHours = [...prev]
                                                                newIrregularStoreHours[idx].valid_from = e.target.value
                                                                return newIrregularStoreHours
                                                            })
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label="終了日"
                                                        disabled={irregularStoreHours[idx]._destroy}
                                                        helperText={irregularStoreHours[idx]._destroy ? '削除候補' : ''}
                                                        sx={{ width: desktop ? '46%' : '8rem', marginBottom: '1em' }}
                                                        value={sh.valid_through}
                                                        onChange={e => {
                                                            setIrregularStoreHours(prev => {
                                                                const newIrregularStoreHours = [...prev]
                                                                newIrregularStoreHours[idx].valid_through = e.target.value
                                                                return newIrregularStoreHours
                                                            })
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        type="time"
                                                        label="開店時間"
                                                        disabled={irregularStoreHours[idx]._destroy}
                                                        helperText={irregularStoreHours[idx]._destroy ? '削除候補' : ''}
                                                        sx={{ width: desktop ? '46%' : '8rem', marginBottom: '1em' }}
                                                        value={sh.open_time}
                                                        inputProps={{ step: 900 }}
                                                        InputLabelProps={{ shrink: true }}
                                                        onChange={e => {
                                                            setIrregularStoreHours(prev => {
                                                                const newIrregularStoreHours = [...prev]
                                                                newIrregularStoreHours[idx] = {
                                                                    ...newIrregularStoreHours[idx],
                                                                    open_time: e.target.value
                                                                }
                                                                return newIrregularStoreHours
                                                            })
                                                        }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        type="time"
                                                        label="閉店時間"
                                                        disabled={irregularStoreHours[idx]._destroy}
                                                        helperText={irregularStoreHours[idx]._destroy ? '削除候補' : ''}
                                                        sx={{ width: desktop ? '46%' : '8rem', marginBottom: '1em' }}
                                                        value={sh.close_time}
                                                        inputProps={{ step: 900 }}
                                                        InputLabelProps={{ shrink: true }}
                                                        onChange={e => {
                                                            setIrregularStoreHours(prev => {
                                                                const newIrregularStoreHours = [...prev]
                                                                newIrregularStoreHours[idx] = {
                                                                    ...newIrregularStoreHours[idx],
                                                                    close_time: e.target.value
                                                                }
                                                                return newIrregularStoreHours
                                                            })
                                                        }}
                                                    />
                                                </div>
                                            </Stack>
                                            <IconButton
                                                sx={{ width: '0.5em', marginBottom: '1.5em' }}
                                                onClick={() => {
                                                    setIrregularStoreHours(prev => {
                                                        const newIrregularStoreHours = [...prev]
                                                        if (newIrregularStoreHours[idx]._destroy) {
                                                            newIrregularStoreHours[idx] = {
                                                                ...newIrregularStoreHours[idx],
                                                                _destroy: false
                                                            }
                                                        } else {
                                                            newIrregularStoreHours[idx] = {
                                                                ...newIrregularStoreHours[idx],
                                                                _destroy: true
                                                            }
                                                        }
                                                        return newIrregularStoreHours
                                                    })
                                                }}
                                            >
                                                {irregularStoreHours[idx]._destroy ? <RestoreFromTrash /> : <Delete />}
                                            </IconButton>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
                <Card sx={{ p: isMobile() ? '0 1rem' : 2, width: isMobile() ? '90vw' : '50%', boxShadow: isMobile() && 'none'}} >
                    <Typography variant="h6" sx={{ mb: 2 }}>HLG表</Typography>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                        <Button
                            variant="outlined"
                            color="paloBlue"
                            onClick={handleCopyRevenueStaffings}
                            disabled={copyingStaffings || !isAdmin}
                            startIcon={copyingStaffings ? <CircularProgress size={20} /> : <ContentCopyOutlined />}
                            sx={{ width: desktop ? '45em' : '95%' }}
                        >
                            {copyingStaffings ? 'コピー中...' : '店舗タイプのHLG表をコピー'}
                        </Button>
                    </div>
                    {revenueStaffingRows?.length === 0 && (
                        <Alert severity="info" sx={{ width: desktop ? '45em' : '95%', margin: '0 auto 1rem auto' }}>
                            現在、店舗固有のHLG表が設定されていません。店舗タイプのHLG表をコピーして、店舗固有の設定を作成できます。
                        </Alert>
                    )}
                    

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {hlgError && (
                            <Alert severity="error" onClose={() => setHlgError(false)}>
                                {store?.name}{' '}
                                の情報を更新出来ません。「モデル（人数）」の数値を除く1以上の半角数字を入力してください。
                            </Alert>
                        )}
                        <Table sx={{ width: desktop ? '45rem' : '95%', marginTop: '1rem' }} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '5rem' }} align="center">
                                        売上（円）
                                    </TableCell>
                                    <TableCell sx={{ width: '5rem' }} align="center">
                                        必要人数
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                                <TableBody>
                                    {revenueStaffingRows?.length > 0 && 
                                        revenueStaffingRows?.sort((a, b) => a.hourly_rev_high - b.hourly_rev_high).map(e => {
                                            return (
                                                <TableRow key={e.hourly_rev_high}>
                                                    <TableCell align="center">
                                                        {e.hourly_rev_low?.toLocaleString('ja-JP')} - {e.hourly_rev_high?.toLocaleString('ja-JP')}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Input
                                                            defaultValue={e.workers_required}
                                                            sx={{
                                                                width: '2rem',
                                                                backgroundColor:
                                                                    errorRev[e.hourly_rev_high] &&
                                                                    'rgba(240, 58, 58, .4)'
                                                            }}
                                                            disabled={!isAdmin}
                                                            inputProps={{
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            onBlur={event =>
                                                                createUpdateRevenueStaffing(event.target.value, e)
                                                            }
                                                            onKeyPress={event =>
                                                                event.key === 'Enter' && event.target.blur()
                                                            }
                                                            error={errorRev[e.hourly_rev_high]}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                </TableBody>


                        </Table>
                        <Stack direction="row" spacing={2} sx={{ m: '2rem 2rem 6rem 2rem' }}>
                            <TextField
                                label="最大値を変更する"
                                defaultValue={revenueStaffingRows?.[revenueStaffingRows.length - 1]?.hourly_rev_high}
                                sx={{ width: '8rem' }}
                                disabled={!isAdmin}
                                onChange={e => setNewMaxRev(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                disabled={newMaxRev === null}
                                onClick={handleUpdateMaxRev}
                                color='paloBlue'
                            >
                                保存する
                            </Button>
                        </Stack>
                    </div>
                </Card>
                <Box style={{ backgroundColor: 'white', width: '100%', position: 'fixed', bottom: 0, marginLeft: !isMobile() && '-2rem', zIndex: isMobile() ? 1001 : 1, marginBottom: isMobile() && '3rem' }}>
                    <Divider />
                    <Box style={{ display: 'flex', justifyContent: 'left', margin: '1rem' }}>
                        <Button
                            variant="contained"
                            color="paloBlue"
                            onClick={() => {
                                setConfirmationOpen(true)
                            }}
                            style={{ marginLeft: '2rem' }}
                            disabled={saveButtonDisabled || Object.keys(errorRev).length > 0}
                        >
                            変更内容を保存する
                        </Button>
                        <Button variant='outlined' sx={{ ml: 2 }} onClick={handleReturn}>
                            キャンセル
                        </Button>
                    </Box>
                    <Dialog
                        open={confirmationOpen}
                        onClose={() => {
                            setConfirmationOpen(false)
                        }}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                        disableEscapeKeyDown={true}
                        onClick={e => {
                            e.stopPropagation()
                        }}
                    >
                        {saveButtonDisabled ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: '1rem'
                                }}
                            >
                                <CircularProgress />
                                <div>変更を保存中…</div>
                            </div>
                        ) : (
                            <>
                                <DialogContent>
                                    <DialogContentText id="alert-dialog-description">
                                        変更内容を保存しますか？
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setConfirmationOpen(false)}>キャンセル</Button>
                                    <Button onClick={saveStore} autoFocus>
                                        OK
                                    </Button>
                                </DialogActions>
                            </>
                        )}
                    </Dialog>
                    <Dialog
                        open={archiveDialogOpen}
                        onClose={() => setArchiveDialogOpen(false)}
                        aria-labelledby="archive-dialog-title"
                        aria-describedby="archive-dialog-description"
                    >
                        <DialogTitle id="archive-dialog-title">
                            店舗のアーカイブ
                        </DialogTitle>
                        <DialogContent>
                            {employeeCount > 0 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    この店舗には{employeeCount}名の従業員が紐づいています。アーカイブしても従業員のデータは保持されますが、この店舗は一覧から非表示になります。
                                </Alert>
                            )}
                            <DialogContentText id="archive-dialog-description">
                                {store?.name}をアーカイブしますか？アーカイブすると、店舗一覧から非表示になり、新しい従業員を追加することができなくなります。
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiving}>
                                キャンセル
                            </Button>
                            <Button 
                                onClick={handleArchiveStore} 
                                color="error" 
                                variant="contained"
                                disabled={archiving}
                                startIcon={archiving ? <CircularProgress size={20} /> : null}
                            >
                                {archiving ? 'アーカイブ中...' : 'アーカイブする'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Stack>
        </>
    )
}

export default EditStore
